/**
 * Where arranging starts: the score's own notes, kept and handed to the players; only its
 * tune and its chords, with every other part written again under the tune; or only its tune,
 * with the note model writing the parts under it.
 *
 * The score is where it starts until another start is asked for. Starting from the tune keeps
 * nothing of the score's other parts, so the choices that shape those notes -- the harmony
 * level, the texture, a counter-melody, the tune varied and the options -- are shut while it is
 * on, and the check, which moves the arranger's notes among the source's, is not offered. What
 * decides the texture then is the AI arranger's level: Off is the book's own, Light and Normal
 * the models'. The lead-sheet writer voices players only, so a roster with a player of two hands is
 * refused before the run rather than after it.
 *
 * The note model is a model of our own, fitted on string quartets: it is shown the tune and
 * writes a quartet's other parts under it, which the players asked for play from the places
 * they are given in it, and the check then holds what it wrote to the book's rules. It takes
 * none of the choices and no AI arranger level, the check is always made, and each take is
 * another writing of it. A player of two hands is written every part of it, for its two hands, a
 * guitar what its left hand can stop of them at once, and a bowed string alone stops one note
 * of them under each note of its tune. A roster it does not write for (`modelWrites`) -- one
 * player alone who sounds a note at a time -- is refused before the run. What it is told of
 * the score beyond the tune is its add-ons' (`ArrangeHints`), each a switch of its own, with
 * the layers told and their strength beside.
 */

import { ARRANGE_STARTS, handedIn, modelWrites, type ArrangeStart } from "@app/shared-types";
import ArrangeMenu, { type Named } from "./ArrangeMenu";

/** Each start as the menu names it. Keyed by start so a new one cannot go unnamed. */
export const START_NAMES: Named<ArrangeStart> = {
  score: {
    label: "The score",
    title:
      "Keep the score's own notes and hand them to the players: every choice beside this applies, and the check can follow.",
  },
  tune: {
    label: "The tune",
    title:
      "Arrange the passage anew from its tune and its chords: the tune is written as the score has it and every other part is written again under it, the texture decided at the AI arranger's level (Off: the book's own). The score's other parts are not kept, so the choices that shape them are shut and the check is not offered. Players only: a piano, a harp, an organ or a celesta plays the score's own notes.",
  },
  model: {
    label: "The note model",
    title:
      "Keep only the score's tune and have the note model, a model of our own fitted on string quartets, write the other players' parts under it; the check then holds what it wrote to the book's rules. It writes a string quartet, and players other than two violins, a viola and a cello each play one of its parts in the octave they reach; a pianist, a harpist, an organist or a celesta's player plays all of them, each note in the hand that can take it, a guitar what its left hand can stop of them at once, and a bowed string alone stops one note of them under each note of its tune. The add-ons beside the check say what it is told of the score's accompaniment and how far; with all of them off it is shown the tune alone. The choices beside this and the AI arranger are shut, the check is always made, and Another take is another writing. Two players or more, or a piano, a harp, an organ, a celesta, a guitar or a bowed string alone.",
  },
};

/** A start that keeps only the tune, and so takes none of the rules arranger's choices. */
export type Anew = Exclude<ArrangeStart, "score">;

/** The start as one that keeps only the tune, or `null` where the score is started from. */
export function anewOf(start: ArrangeStart): Anew | null {
  return start === "score" ? null : start;
}

/** Why the rules arranger's choices are shut while the score is not started from. */
export const SHUT: Readonly<Record<Anew, string>> = {
  tune: "Starting from the tune takes none of the choices that shape the score's own notes. Start from the score to use them; the choices set are kept for then.",
  model:
    "Starting from the note model takes none of the choices that shape the score's own notes: the model writes every part under the tune. Start from the score to use them; the choices set are kept for then.",
};

/** What becomes of the check while the score is not started from, said on its box. */
export const CHECK_SAID: Readonly<Record<Anew, string>> = {
  tune: "The check moves the arranger's own notes among the score's, and starting from the tune keeps none of the score's other parts: it is not offered when starting from the tune.",
  model:
    "Starting from the note model the check is always made: it moves or leaves out the model's notes where the book's rules flag a chord, gives a waiting player a note where spacing is flagged, and keeps the change only where the checker then finds fewer faults. The tune is never touched.",
};

/** Why the AI arranger is shut while starting from the note model. */
export const MODEL_AI_SHUT =
  "The AI arranger plans the notes the rules write, and starting from the note model the rules write none: it is left off.";

/** What the start does, said under the toolbar while it is on. */
export const SAID: Readonly<Record<Anew, string>> = {
  tune: "Starting from the tune arranges the passage anew from its tune and chords: the score's other parts are not kept, and the choices that shape them are shut.",
  model:
    "Starting from the note model keeps only the tune's notes: the model writes the parts under it and the check holds them to the book's rules. The choices and the AI arranger are shut.",
};

/** Why a roster cannot start where `start` says, or `null` where it can. */
export function unseatable(roster: readonly string[], start: ArrangeStart): string | null {
  const two = handedIn(roster).map((one) => one.label.toLowerCase());
  if (start === "tune" && two.length > 0) {
    const named = two.join(" and the ");
    return `Starting from the tune seats players only: the ${named} ${two.length > 1 ? "play" : "plays"} the score's own notes. Take the ${named} off the roster, or start from the score.`;
  }
  if (start === "model" && roster.length > 0 && !modelWrites(roster)) {
    return "The note model writes the parts under the tune, so it needs a player beside the one who carries it, or one who sounds more than its tune: a player of two hands, a guitar or a bowed string. Add a player, or start from the score.";
  }
  return null;
}

export default function ArrangeStartMenu({
  value,
  onPick,
}: {
  readonly value: ArrangeStart;
  onPick(value: ArrangeStart): void;
}): JSX.Element {
  return (
    <ArrangeMenu
      label="Start from"
      value={value}
      values={ARRANGE_STARTS}
      names={START_NAMES}
      onPick={onPick}
    />
  );
}
